import {UI} from "../../../../../../stem-core/src/ui/UIBase";
import {StyleSheet} from "../../../../../../stem-core/src/ui/Style";

import {styleRule} from "../../../../../../stem-core/src/decorators/Style";
import {Messages} from "../../../../../Messages";
import {registerStyle} from "../../../../../../stem-core/src/ui/style/Theme";
import {iFrameState} from "../../../../../services/IFrameState";
import {Money} from "../../../../../../stem-core/src/localization/Money.js";
import {CheckboxInput} from "../../../../../../core/ui/input/checkbox/CheckboxInput.jsx";
import {Flow} from "../../../../flows/Flow.js";
import {MERCHANT_SCROLLABLE_CONTENT_HEIGHT} from "../../../../../widget/WidgetConstants";
import {isSmallScreen} from "../../../../../UtilsLib";
import {DonateActiveButtonList} from "../../../../../common/donate/DonateActiveButtonList";
import {DonateAmountButtonList} from "../../../../../common/donate/DonateAmountButtonList";
import {iFrameMerchantService} from "../../../../../services/IframeMerchantService";
import {iFrameUserDataService} from "../../../../../services/IFrameUserDataService";
import {autoredraw} from "../../../../../../stem-core/src/decorators/AutoRedraw";
import {MessageElement} from "../../../../../widget/ui/MessageElement";
import {BasePanelPageStyle} from "../../../../PanelPage.jsx";


class DonateComponentStyle extends StyleSheet {
    @styleRule
    container = {
        overflow: () => isSmallScreen() ? "auto" : "",
        maxHeight: () => isSmallScreen() ? MERCHANT_SCROLLABLE_CONTENT_HEIGHT : "",
        paddingRight: () => isSmallScreen() ? 18 : "",
        marginRight: () => isSmallScreen() ? -18 : "",
        ">:not(:last-child)": {
            marginBottom: 12,
        }
    };

    @styleRule
    coverFeesCheckbox = {
        marginTop: 12,
        fontSize: this.themeProps.MERCHANT_FONT_SIZE_NORMAL + " !important",
        marginBottom: 18,
        color: this.themeProps.MERCHANT_TEXT_SECONDARY_COLOR + " !important",
    };

    @styleRule
    frequencyButtons = {
        fontSize: this.themeProps.MERCHANT_FONT_SIZE_NORMAL,
    };

    @styleRule
    customAmountError = {
        textAlign: "center",
        fontSize: this.themeProps.MERCHANT_FONT_SIZE_SMALL,
        height: 15,
        color: this.themeProps.MERCHANT_ERROR,
    };
}

@autoredraw(iFrameUserDataService)
@registerStyle(DonateComponentStyle)
export class DonateComponent extends UI.Element {
    customInputError = "";
    donationIndex = null;
    defaultCustomAmount = null;

    formatAmount(amount) {
        return new Money(amount, iFrameMerchantService.getDonationOffer().getCurrency()).toString();
    }

    getDonationIndex() {
        const index = iFrameMerchantService.getDonationOffer().predefinedAmounts.indexOf(Flow.donationDetails.getDonationAmount());
        return index === -1 ? iFrameMerchantService.getDonationOffer().predefinedAmounts.length : index;
    }

    handleDonationAmountChange(amount, isCustomAmount) {
        Flow.donationDetails.donationAmount = amount;
        Flow.donationDetails.isCustomAmount = isCustomAmount;
        this.dispatchChange();
        this.redraw();
    }

    handleFrequencyChange(frequency) {
        Flow.donationDetails.frequency = frequency;
        this.dispatchChange();
        this.redraw();
    }

    // Some of these are called during renders and we want to make sure that the render is done by the time it is
    // dispatched, because the outer components might trigger another render during this dispatch.
    dispatchChange() {
        if (!this.changeDispatchEnqueued) {
            this.changeDispatchEnqueued = true;
            queueMicrotask(() => {
                this.changeDispatchEnqueued = false;
                this.dispatch("change");
            });
        }
    }

    recurringOptionsAvailable() {
        return !iFrameUserDataService.getActiveRecurringDonation();
    }

    render() {
        const {styleSheet} = this;
        const panelStyleSheet = BasePanelPageStyle.getInstance(this.getTheme());

        const offer = iFrameMerchantService.getDonationOffer();
        const {allowCustomAmount, predefinedAmounts, minAmount, maxAmount} = offer;
        const frequencyOptions = offer.getFrequencyOptions();

        if (this.donationIndex == null) {
            this.donationIndex = this.getDonationIndex();
        }
        const isCustomAmount = this.getDonationIndex() === predefinedAmounts.length;
        if (this.defaultCustomAmount == null) {
            if (isCustomAmount) {
                this.defaultCustomAmount = Flow.donationDetails.donationAmount;
            } else {
                this.defaultCustomAmount = "";
            }
        } else if (!isCustomAmount) {
            this.defaultCustomAmount = "";
        }

        if (!Flow.donationDetails.frequency && !this.recurringOptionsAvailable()) {
            Flow.donationDetails.frequency = frequencyOptions[0];
            this.dispatchChange();
        }


        const onCustomInputError = (errorMessage) => {
            this.customInputError = errorMessage;
        };

        const onValidCustomInput = () => {
            this.customInputError = "";
            this.redraw();
        };

        const customAmountButtonOptions = {
            currency: iFrameMerchantService.getDonationOffer().getCurrency(),
            minAmount: minAmount,
            maxAmount: maxAmount,
            defaultCustomAmount: this.defaultCustomAmount,
        };

        const showFrequencies = this.recurringOptionsAvailable() && offer.hasFrequencyChoice();

        return [
            <div className={panelStyleSheet.title}>
                {Messages.donationDetails}
            </div>,

            offer.hasAmountChoice() && <DonateAmountButtonList
                defaultActiveButtonIndex={this.donationIndex}
                items={predefinedAmounts.map(amount => ({
                        label: this.formatAmount(amount),
                        value: amount,
                    })
                )}
                onCustomInputError={onCustomInputError}
                onValidCustomInput={onValidCustomInput}
                rowSizeLimit={isSmallScreen() ? 3 : 5}
                customAmountButtonOptions={allowCustomAmount && customAmountButtonOptions}
                onSelect={({amount, isCustomAmount}) => this.handleDonationAmountChange(amount, isCustomAmount)}
            />,

            allowCustomAmount && <div className={styleSheet.customAmountError}>
                {this.customInputError}
            </div>,

            iFrameState.allowUserToCoverFees && <CheckboxInput
                ref="payDonationFeeCheckbox"
                className={styleSheet.coverFeesCheckbox}
                testId="payDonationFeeCheckbox"
                initialValue={Flow.donationDetails.payDonationFeeCheckboxChecked}
                onChange={(enabled) => {
                    Flow.donationDetails.payDonationFeeCheckboxChecked = enabled;
                    this.dispatchChange();
                }}
                label={Messages.payDonationFeeCheckbox(this.formatAmount(Flow.donationDetails.getDonationFee()))}/>,

            <MessageElement message={Messages.donationExplanationMessage}/>,

            showFrequencies && <DonateActiveButtonList
                defaultActiveButtonIndex={frequencyOptions.indexOf(Flow.donationDetails.getFrequency())}
                className={styleSheet.frequencyButtons}
                rowSizeLimit={5}
                onSelect={(frequency) => this.handleFrequencyChange(frequency)}
                items={frequencyOptions.map(frequency => ({
                    label: frequency.getFrequencyName(),
                    value: frequency
                }))}/>,
        ];
    }
}
